import { v4 as uuid }                                                        from 'uuid';
import _                                                                     from 'underscore';
import classNames                                                            from 'classnames';
import React, { ReactNode }                                                  from 'react';
import { FieldArray, FieldArrayRenderProps, FormikErrors, useFormikContext } from 'formik';
import { Button, Form }                                                      from 'semantic-ui-react';
import { ClassValue }                                                        from 'classnames/types';

import { FlexAlign, Uuid }                       from '../../../../types';
import { DraggableRemovableItem }                from '../../draggable';
import { DraggableRemovableItemProps }           from '../../draggable/DraggableRemovableItem';
import { EmptyCollectionMessage }                from '../../util/messaging/EmptyCollectionMessage';
import { FieldWrapper, FormArrayErrorContainer } from '../formik';
import { FieldDescription }                      from '../formik/FieldDescription';
import { FieldLabel }                            from '../formik/FieldLabel';
import { Cell, Grid }                            from 'styled-css-grid';


type FieldComponentRenderFunction<T> = (
    arrayHelpers: FieldArrayRenderProps,
    addItemHandler: () => void,
    removeItemHandler: MultipleInputArrayFieldProps<T>['onRemoveItem']
) => ReactNode

interface MultipleInputArrayFieldProps<T = object> {
    name: string,
    label?: string,
    descriptionText?: ReactNode,
    sortBy?: string | ( ( item: T, index: number ) => string ),
    addLabel?: string,
    addDisabled?: boolean,
    addVisible?: boolean,
    removeDisabled?: boolean | Function,
    removeDisableFirstOnly?: boolean,
    removeDisabledOnIndexes?: number[] | Function,
    emptyComponent?: ReactNode,
    listContainerClasses?: ClassValue,
    headerContainerClasses?: ClassValue,
    draggableRemovableContainerProps?: DraggableRemovableItemProps,
    fieldHeaderComponent?: ReactNode | FieldComponentRenderFunction<T>,
    persistFieldHeaderComponent?: boolean,

    objectFactory?(): T,

    onRemoveItem?( item: T, index: number ): void,

    onAddItem?( item: T ): void,

    onBeforeAddItem?( addMethod: () => void ): void,

    renderField?( item: T, index: number ): ReactNode,
}

const defaultProps: MultipleInputArrayFieldProps<{ id: Uuid }> = {
    name:                             null,
    label:                            'Multi Field Array',
    descriptionText:                  null,
    sortBy:                           null,
    addLabel:                         'Add Item',
    addDisabled:                      false,
    addVisible:                       true,
    removeDisabled:                   false,
    removeDisableFirstOnly:           false,
    removeDisabledOnIndexes:          null,
    onAddItem:                        null,
    onBeforeAddItem:                  null,
    onRemoveItem:                     null,
    objectFactory:                    () => ( { id: uuid() } ),
    renderField:                      React.memo( ( item, index ) => { return null; } ),
    emptyComponent:                   <EmptyCollectionMessage/>,
    listContainerClasses:             null,
    headerContainerClasses:           null,
    draggableRemovableContainerProps: {},
    fieldHeaderComponent:             null,
    persistFieldHeaderComponent:      false,
};

const FormField = <T, >( props: MultipleInputArrayFieldProps<T> ) => {

    let formik = useFormikContext();

    let formikErrors: FormikErrors<unknown> = formik.errors,
        formikValues: any                   = formik.values,
        errors: unknown                     = formikErrors,
        values: unknown[]                   = _.get( formikValues, props.name );

    values = _.sortBy( values, ( item, index ) => (
            props.sortBy
            ? _.isFunction( props.sortBy )
              ? props.sortBy( item as T, index )
              : props.sortBy
            : undefined
        )
    );

    let headerContainerClasses = classNames( {
        'margin-xs-bottom': !props.headerContainerClasses
    }, props.headerContainerClasses );

    let listContainerClasses = classNames( {
        'padding-sm container--colored-bg-lunar': !props.listContainerClasses,
    }, props.listContainerClasses );

    const handleAddItem = ( arrayHelpers: FieldArrayRenderProps ) => {

        let newItem = props.objectFactory();

        arrayHelpers.push( newItem );

        if( _.isFunction( props.onAddItem ) ){

            props.onAddItem( newItem );
        }
    };

    const handleBeforeAddItem = ( internalAddMethod ) => {

        if( _.isFunction( props.onBeforeAddItem ) ){

            props.onBeforeAddItem( internalAddMethod );
        }
    };

    const handleRemoveItem = ( item, index, arrayHelpers: FieldArrayRenderProps ) => {

        arrayHelpers.remove( index );

        if( _.isFunction( props.onRemoveItem ) ){
            props.onRemoveItem( item, index );
        }

    };

    return (
        <Form.Field>
            <FieldArray
                name={ props.name }
                render={ arrayHelpers => {

                    const addMethod       = () => handleAddItem( arrayHelpers ),
                          beforeAddMethod = () => handleBeforeAddItem( addMethod ),
                          hasPreAddMethod = _.isFunction( props.onBeforeAddItem );

                    return (
                        <>

                            { ( props.label || props.descriptionText || props.addVisible ) && (

                                <Grid columns={ `1fr auto` } columnGap={ `50px` } className={ headerContainerClasses }>

                                    <Cell middle>

                                        <FieldWrapper>
                                            { props.label && <FieldLabel>{ props.label }</FieldLabel> }

                                            { props.descriptionText && <FieldDescription>{ props.descriptionText }</FieldDescription> }
                                        </FieldWrapper>

                                    </Cell>
                                    <Cell middle>

                                        { props.addVisible && (
                                            <Button compact
                                                    basic={ props.addDisabled }
                                                    secondary={ !props.addDisabled }
                                                    size={ 'tiny' }
                                                    floated={ 'right' }
                                                    type="button"
                                                    icon={ 'plus' }
                                                    disabled={ props.addDisabled }
                                                    content={ props.addLabel }
                                                    onClick={ hasPreAddMethod ? beforeAddMethod : addMethod }
                                            />
                                        ) }

                                    </Cell>

                                </Grid>
                            ) }

                            <div className={ listContainerClasses }>

                                <FormArrayErrorContainer error={ errors } padded={ _.isString( errors ) }>

                                    { ( !_.isEmpty( values ) || props.persistFieldHeaderComponent ) && ( typeof props.fieldHeaderComponent === 'function' )
                                      ? props.fieldHeaderComponent( arrayHelpers, () => handleAddItem( arrayHelpers ), props.onRemoveItem )
                                      : props.fieldHeaderComponent }

                                    { values.length

                                      ? values.map( ( item: T, index ) => {

                                             let itemProhibitsRemoval        = ( _.isFunction( props.removeDisabled ) ? props.removeDisabled( item ) : props.removeDisabled ),
                                                 indexedItemProhibitsRemoval = ( _.isFunction( props.removeDisabledOnIndexes )
                                                                                 ? props.removeDisabledOnIndexes( values ).includes( index )
                                                                                 : ( props.removeDisabledOnIndexes ?? [] ).includes( index ) ),
                                                 firstItemProhibitsRemoval   = ( props.removeDisableFirstOnly && index === 0 );

                                             let canRemove = !( itemProhibitsRemoval || firstItemProhibitsRemoval || indexedItemProhibitsRemoval );

                                             return (

                                                 <DraggableRemovableItem
                                                     key={ `${ props.name }-${ index }` }
                                                     hasHandles={ false }
                                                     align={ FlexAlign.START }
                                                     className={ 'margin-xs-bottom' }
                                                     handlesContainerClasses={ 'margin-xs-top' }
                                                     removeContainerClasses={ 'margin-xs-top' }
                                                     hasRemove={ canRemove }
                                                     onClick={ () => handleRemoveItem( item, index, arrayHelpers ) }
                                                     { ...props.draggableRemovableContainerProps }>

                                                     { props.renderField( item, index ) }

                                                 </DraggableRemovableItem>

                                             );

                                         } ) : (

                                          props.emptyComponent

                                      )
                                    }


                                </FormArrayErrorContainer>

                            </div>
                        </>
                    );
                } }
            />
        </Form.Field>
    );
};

FormField.defaultProps = defaultProps;

export { FormField as MultipleInputArrayField };
